import {
  SIMPLE,
  COMPLEX,
  TIMER,
  CARRY_LOGIC_SET,
  CARRY_TIMER,
  SELF_REFERENCE,
  DIGITAL_INPUT,
  ANALOG_INPUT,
  CAN_INPUT,
  CAN_CONDITION,
  NATIVE_CONDITION,
  DIGITAL_CONDITION,
  ANALOG_CONDITION,
  NATIVE_INPUT,
  SIXTEEN_BIT_OUTPUT,
  SWITCH_INPUT,
} from 'utils/constants'
import { length, includes } from 'ramda'
import strings from 'utils/strings'
import { translationValue } from 'utils/translations'
import { timerValue } from 'utils/timers'
import { opMap } from 'components/utils'
import {
  configurableInputValueLabel,
  getSwitchPositionLabel,
} from 'components/feature/utils'

import { signalTranslationRangeSymbolLabel } from 'i18n/displayConstants/signals'

/**
 * Extracts the pin number from an analog input description and formats it
 * as "AI Pin XX" where XX is zero-padded to 2 digits.
 * @param {string} description - The signal description (e.g., "Analog Input 1", "Analog Input 12")
 * @returns {string} - The formatted short label (e.g., "AI Pin 01", "AI Pin 12")
 */
export const formatAnalogInputPinLabel = (description) => {
  if (!description) return null
  // Extract the number from the description (e.g., "Analog Input 1" -> "1")
  const match = description.match(/(\d+)/)
  if (!match) return null
  const pinNumber = parseInt(match[1], 10)
  // Zero-pad to 2 digits
  const paddedNumber = pinNumber.toString().padStart(2, '0')
  return `${strings.analog_pin_input} ${paddedNumber}`
}

/**
 * Extracts the pin number from a digital input description and formats it
 * as "DI Pin XX" where XX is zero-padded to 2 digits.
 * @param {string} description - The signal description (e.g., "Digital Input 1", "Digital Input 12")
 * @returns {string} - The formatted short label (e.g., "DI Pin 01", "DI Pin 12")
 */
export const formatDigitalInputPinLabel = (description) => {
  if (!description) return null
  // Extract the number from the description (e.g., "Digital Input 1" -> "1")
  const match = description.match(/(\d+)/)
  if (!match) return null
  const pinNumber = parseInt(match[1], 10)
  // Zero-pad to 2 digits
  const paddedNumber = pinNumber.toString().padStart(2, '0')
  return `${strings.digital_pin_input} ${paddedNumber}`
}

const massageRungType = (
  isSixteenBitCanOutput,
  ignoreCarry,
  rungType,
  simulate,
  subType
) => {
  if (isSixteenBitCanOutput) {
    return SIXTEEN_BIT_OUTPUT
  }
  if (rungType === NATIVE_INPUT && subType === SWITCH_INPUT) {
    return SWITCH_INPUT
  }
  if (!ignoreCarry) {
    return rungType
  }
  if (
    !simulate &&
    (rungType === CARRY_LOGIC_SET || rungType === SELF_REFERENCE)
  ) {
    return COMPLEX
  }
  if (rungType === CARRY_TIMER) {
    return TIMER
  }
  return rungType
}

const conditionLabelText = ({
  rungType,
  selectedInputDescription,
  operator,
  value,
  inputConfig,
  ignoreCarry,
  translationType,
  units,
  translations = [],
  isSixteenBitCanOutput,
  simulate,
  result,
  subType,
  signal,
  inputUri,
  uriOptionsByPosition = [],
  connector,
  location,
  hasNativeOutput,
  parentQueryUri = '',
  customDescription,
}) => {
  if (!selectedInputDescription) {
    return strings.undefined_condition
  }
  const rungTypeMassaged = massageRungType(
    isSixteenBitCanOutput,
    ignoreCarry,
    rungType,
    simulate,
    subType
  )
  const singleCondition = parentQueryUri === undefined || parentQueryUri === ''
  const positionByInputUri = getSwitchPositionLabel(
    inputUri,
    uriOptionsByPosition
  )
  const translatedValue = translationValue(value, translationType, translations)
  const translatedLabel = units ? signalTranslationRangeSymbolLabel(units) : ''

  const getLabelPrefixMeasureValue = () => {
    if (simulate) {
      return strings.evaluation
    }
    // When custom description is set, use custom name + switch position (Up/Down) if applicable
    if (customDescription) {
      if (positionByInputUri) {
        const inputValueLabel = length(configurableInputValueLabel(inputConfig))
          ? `(${configurableInputValueLabel(inputConfig)})`
          : ''
        return `${customDescription} ${positionByInputUri}${inputValueLabel}`
      }
      return customDescription
    }

    // For Analog Input types, use the short "AI Pin XX" format by default
    const isAnalogInputType = includes(rungTypeMassaged, [
      ANALOG_INPUT,
      ANALOG_CONDITION,
    ])
    if (isAnalogInputType) {
      const shortLabel = formatAnalogInputPinLabel(selectedInputDescription)
      if (shortLabel) {
        return shortLabel
      }
    }

    // For Digital Input types, use the short "DI Pin XX" format by default
    const isDigitalInputType = includes(rungTypeMassaged, [
      DIGITAL_INPUT,
      DIGITAL_CONDITION,
    ])
    if (isDigitalInputType) {
      const shortLabel = formatDigitalInputPinLabel(selectedInputDescription)
      if (shortLabel) {
        return shortLabel
      }
    }

    const connectorLocation = isDigitalInputType
      ? `: ${connector}-${location} `
      : ''
    const inputValueLabel = length(configurableInputValueLabel(inputConfig))
      ? `(${configurableInputValueLabel(inputConfig)})`
      : ''
    if (positionByInputUri) {
      return `${selectedInputDescription}${' '}${positionByInputUri}${inputValueLabel}`
    }

    return `${selectedInputDescription}${connectorLocation}${inputValueLabel}`
  }
  const labelPrefix = getLabelPrefixMeasureValue()
  // determine if sna or error values are defined
  // modify the native input label to include sna or error values
  // otherwise display the translated value
  // console.log({ signal })
  const {
    translation: { definedSnaValues = [], definedErrorValues = [] } = {},
  } = signal || {}
  const hasSnaValue =
    definedSnaValues.length > 0 && definedSnaValues.includes(value)
  const hasErrorValue =
    definedErrorValues.length > 0 && definedErrorValues.includes(value)

  // TODO: move string configuration to i18n folder
  switch (rungTypeMassaged) {
    case NATIVE_INPUT:
      if (hasSnaValue && !hasNativeOutput) {
        return `${labelPrefix} ${opMap[operator]} ${strings.signal_sna}`
      }
      if (hasErrorValue && !hasNativeOutput) {
        return `${labelPrefix} ${opMap[operator]} ${strings.error}`
      }
      return singleCondition && !simulate && hasNativeOutput
        ? `${labelPrefix}`
        : `${labelPrefix} ${opMap[operator]} ${translatedValue} ${translatedLabel}`
    case TIMER:
    case CARRY_TIMER:
      return `${labelPrefix} ${opMap[operator]} ${timerValue(value)}`
    case SIMPLE:
    case SWITCH_INPUT:
    case CAN_INPUT:
    case CAN_CONDITION:
    case NATIVE_CONDITION:
      return hasNativeOutput && parentQueryUri === '' && !simulate
        ? `${labelPrefix} ${translatedLabel}`
        : `${labelPrefix} ${opMap[operator]} ${translatedValue} ${translatedLabel}`
    case DIGITAL_INPUT:
    case DIGITAL_CONDITION: {
      const inputConfigLabel = configurableInputValueLabel(inputConfig)

      // When custom description is set, show: customName = Value (Polarity)
      if (customDescription) {
        const polaritySuffix =
          simulate || !inputConfigLabel ? '' : ` (${inputConfigLabel})`
        if (hasNativeOutput && singleCondition) {
          return `${labelPrefix}${polaritySuffix}`
        }
        return `${labelPrefix} ${opMap[operator]} ${translatedValue}${polaritySuffix}`
      }

      // Default format: DI Pin XX = Value (Polarity)
      const defaultPolaritySuffix =
        simulate || !inputConfigLabel ? '' : ` (${inputConfigLabel})`
      if (hasNativeOutput && singleCondition) {
        return `${labelPrefix}${defaultPolaritySuffix}`
      }
      return `${labelPrefix} ${opMap[operator]} ${translatedValue}${defaultPolaritySuffix}`
    }
    case ANALOG_INPUT:
    case ANALOG_CONDITION: {
      // Get the voltage range label (e.g., "Voltage 0.0 - 6V")
      const voltageRangeLabel = configurableInputValueLabel(inputConfig)
      const voltageRange = voltageRangeLabel ? ` (${voltageRangeLabel})` : ''

      // When custom description is set, show: customName = Value units (range)
      if (customDescription) {
        if (hasNativeOutput && singleCondition && !simulate) {
          return `${labelPrefix} ${translatedLabel}${voltageRange}`
        }
        return `${labelPrefix} ${opMap[operator]} ${value}${translatedLabel}${voltageRange}`
      }

      // Default format: AI Pin XX = Value units (range)
      if (hasNativeOutput && singleCondition && !simulate) {
        return `${labelPrefix} ${translatedLabel}${voltageRange}`
      }
      return `${labelPrefix} ${opMap[operator]} ${value}${translatedLabel}${voltageRange}`
    }
    case CARRY_LOGIC_SET:
      return simulate
        ? `${strings.evaluation}: ${result ? strings.true : strings.false}`
        : selectedInputDescription
    case COMPLEX:
    case SELF_REFERENCE:
    case SIXTEEN_BIT_OUTPUT:
    default:
      return labelPrefix
  }
}

export default conditionLabelText
